import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/eastenderz-hi-ibiza-16-june-2026";
const IMAGE_URL = "https://www.hey-lets-travel.com/assets/community/4ec66bc1-d1dc-4a01-ae9f-45dc413d57ba.png";
const TICKET_URL = "https://www.ibiza-spotlight.com/night/promoters/eastenderz-hi";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const THEATRE_LINEUP = ["East End Dubs B2B Sosa", "Marsolo", "DXNBY"];
const CLUB_ROOM_LINEUP = ["Paco Osuna", "DJ Oliver", "Manu Gonzalez"];
const ALL_ARTISTS = ["East End Dubs", "Sosa", "Marsolo", "DXNBY", ...CLUB_ROOM_LINEUP];

const KEYWORDS = [
  "Eastenderz Hï Ibiza 16 June 2026",
  "East End Dubs B2B Sosa Hï Ibiza",
  "Eastenderz Hï Ibiza tickets",
  "Marsolo Hï Ibiza 2026",
  "DXNBY Ibiza 2026",
  "Paco Osuna NOW HERE Hï Ibiza",
  "DJ Oliver Hï Ibiza",
  "Manu Gonzalez Hï Ibiza",
  "Hï Ibiza Tuesday events 2026",
  "Ibiza house music events June 2026",
  "Ibiza deep tech events 2026",
  "minimal house Ibiza 2026",
  "East End Dubs Ibiza 2026",
  "Eastenderz Ibiza residency 2026",
  "Ibiza tickets and hotel package",
  "HLT Ibiza concierge",
  "Ibiza hotels near Hï Ibiza",
  "Playa d'en Bossa house music events",
];

const FAQS = [
  {
    question: "When is Eastenderz at Hï Ibiza on 16 June 2026?",
    answer: "East End Dubs presents Eastenderz takes place at Hï Ibiza on Tuesday 16 June 2026, with the event listed from 23:30.",
  },
  {
    question: "Who is playing Eastenderz at Hï Ibiza on 16 June?",
    answer: "The Theatre lineup is East End Dubs B2B Sosa, Marsolo and DXNBY. Club Room / NOW HERE features Paco Osuna, DJ Oliver and Manu Gonzalez.",
  },
  {
    question: "What music should I expect at Eastenderz?",
    answer: "Expect deep tech, minimal house, rolling tech house, dubby percussion, garage-influenced groove and a serious underground house music crowd.",
  },
  {
    question: "Where should I stay for Eastenderz at Hï Ibiza?",
    answer: "Playa d'en Bossa is the easiest area for Hï Ibiza. Ibiza Town and Talamanca also work well if you want restaurants, marina energy and easier taxis before or after the event.",
  },
  {
    question: "Can HLT help plan the full Eastenderz trip?",
    answer: "Yes. HLT can help with ticket direction, hotel options, villas, transfers, VIP extras and concierge support around Eastenderz at Hï Ibiza.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Eastenderz at Hï Ibiza 16 June 2026: East End Dubs B2B Sosa, Marsolo, DXNBY and NOW HERE",
  description:
    "East End Dubs presents Eastenderz at Hï Ibiza on Tuesday 16 June 2026 with East End Dubs B2B Sosa, Marsolo, DXNBY, Paco Osuna, DJ Oliver and Manu Gonzalez.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-06-16",
  dateModified: "2026-06-16",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "East End Dubs presents Eastenderz at Hï Ibiza",
  startDate: "2026-06-16T23:30:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "Hï Ibiza",
    address: "Playa d'en Bossa, Ibiza, Spain",
  },
  performer: ALL_ARTISTS.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: TICKET_URL,
    priceCurrency: "EUR",
    price: "30",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "View Eastenderz Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Build the Trip With HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function EastenderzHiIbizaSixteenJuneArticle() {
  return (
    <>
      <Helmet>
        <title>Eastenderz Hï Ibiza 16 June 2026 Tickets | East End Dubs B2B Sosa</title>
        <meta
          name="description"
          content="Eastenderz at Hï Ibiza on Tuesday 16 June 2026 with East End Dubs B2B Sosa, Marsolo, DXNBY, Paco Osuna, DJ Oliver and Manu Gonzalez. Tickets, hotels and HLT Ibiza concierge."
        />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Eastenderz Hï Ibiza 16 June 2026 Tickets | East End Dubs B2B Sosa" />
        <meta property="og:description" content="A music-first HLT guide to Eastenderz at Hï Ibiza on Tuesday 16 June 2026." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Eastenderz Hï Ibiza 16 June 2026 Tickets" />
        <meta name="twitter:description" content="East End Dubs B2B Sosa, Marsolo and DXNBY in The Theatre, with Paco Osuna, DJ Oliver and Manu Gonzalez in Club Room / NOW HERE." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">HLT Essential · Ibiza 2026</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              Eastenderz at Hï Ibiza: 16 June 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              East End Dubs presents Eastenderz at Hï Ibiza on Tuesday 16 June 2026, with East End Dubs B2B Sosa leading The Theatre alongside Marsolo and DXNBY, while Paco Osuna, DJ Oliver and Manu Gonzalez carry Club Room / NOW HERE.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta children="View Eastenderz ticket options" />
              <ConciergeCta children="Plan Hï Ibiza with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>East End Dubs presents Eastenderz</dd></div>
              <div><dt className="text-white">Venue</dt><dd>Hï Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Tuesday 16 June 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>23:30</dd></div>
              <div><dt className="text-white">From</dt><dd>30 EUR</dd></div>
              <div><dt className="text-white">Sound</dt><dd>Deep tech, minimal house, UK tech house and groove-led underground house</dd></div>
            </dl>
          </aside>
        </header>

        <figure className="overflow-hidden border border-brand-500/20 bg-black">
          <img src="/assets/community/4ec66bc1-d1dc-4a01-ae9f-45dc413d57ba.png" alt="East End Dubs presents Eastenderz at Hï Ibiza 16 June 2026 artwork" className="w-full object-cover" />
        </figure>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr),20rem]">
          <div className="space-y-8">
            <ArticleSection title="Why this Eastenderz date matters">
              <p>
                Tonight's Eastenderz date is exactly the type of Ibiza event HLT is built to cover: a music-first Tuesday night where the lineup, the room and the audience all make sense together. This is not a generic holiday club night. It is a deep tech and minimal house programme for people who know why they are going to Hï Ibiza.
              </p>
              <p>
                The headline moment is East End Dubs B2B Sosa. East End Dubs has built Eastenderz into one of the clearest brands in underground house, known for stripped-back percussion, rolling basslines and a loyal crowd that follows the sound across cities and festivals. Sosa brings a more direct UK tech-house punch: tight drums, vocal hooks, heavy low end and peak-time pressure that can make a big room move quickly.
              </p>
              <p>
                Around that back-to-back, Marsolo and DXNBY give The Theatre extra depth. Marsolo brings modern European groove and cleaner rolling house energy, while DXNBY fits the newer wave of selectors moving through deep tech, garage-influenced rhythm and underground house spaces. It is a lineup designed for momentum rather than decoration.
              </p>
            </ArticleSection>

            <ArticleSection title="The Theatre: East End Dubs B2B Sosa, Marsolo and DXNBY">
              <p>
                The Theatre at Hï Ibiza is built for scale. When a night like Eastenderz lands there, the challenge is keeping the underground identity intact while still filling a huge room. That is why the East End Dubs B2B Sosa pairing is so important. East End Dubs brings the Eastenderz DNA. Sosa brings a sharper, more immediate club weapon style. Together, the set should balance rolling deep-tech pressure with direct peak-time energy.
              </p>
              <p>
                Marsolo gives the bill a smoother European groove language. His records and sets usually sit in that pocket where the drums are clean, the bassline moves, and the track selection keeps the room locked without forcing obvious commercial moments. If East End Dubs and Sosa are the headline spark, Marsolo is the kind of artist who keeps the room breathing properly.
              </p>
              <p>
                DXNBY adds another layer for the newer underground crowd. Expect a sound that sits close to deep tech, minimal and rolling house rather than EDM-style peaks. For people researching Ibiza events before they book, this is the sort of name that tells you the programming is still rooted in club culture.
              </p>
            </ArticleSection>

            <ArticleSection title="Club Room / NOW HERE: Paco Osuna, DJ Oliver and Manu Gonzalez">
              <p>
                The second part of the night is just as important. Club Room / NOW HERE brings Paco Osuna, DJ Oliver and Manu Gonzalez into the same building, meaning the event is not only about The Theatre. It gives the night another identity: darker, more driving, more techno-leaning, and ideal for people who want to move between rooms rather than sit on one sound all night.
              </p>
              <p>
                Paco Osuna is the anchor. His reputation comes from years of serious club work, long-form DJ sets and a sound that sits between techno pressure and groove-led movement. He is the kind of artist who gives a room weight. DJ Oliver adds history and credibility: a name tied into Ibiza's club culture over a long period, not just a one-season booking. Manu Gonzalez brings the local Ibiza connection and a style that understands the island's dancefloors properly.
              </p>
              <p>
                That combination makes the 16 June date feel bigger than a single-room party. You can start with the Eastenderz groove in The Theatre, then move into NOW HERE for a deeper, more driving Club Room energy later in the night.
              </p>
            </ArticleSection>

            <ArticleSection title="What to listen to before you go">
              <p>
                Streaming charts move constantly, so treat this as a listening map rather than a fixed ranking. For East End Dubs, start with the Eastenderz catalogue, recent Eastenderz mixes and longer-form sets where the reduced drums, bass movement and dubby details make more sense than a single short clip.
              </p>
              <p>
                For Sosa, search his highest-streamed tech-house records and recent club edits before heading out. His sound is usually more immediate: punchy bass, vocal energy and peak-time simplicity that works fast in a large room. For Marsolo, listen for rolling, groove-led house and minimal cuts with smoother swing. DXNBY is worth checking through recent mixes and underground house selections rather than only single tracks.
              </p>
              <p>
                On the Club Room side, Paco Osuna's Mindshake world is the best entry point, especially if you want to understand the more techno-leaning energy of NOW HERE. DJ Oliver and Manu Gonzalez are both worth exploring through Ibiza-focused mixes, because their value is not only individual tracks; it is how they programme a room on the island.
              </p>
            </ArticleSection>

            <ArticleSection title="Why Tuesday at Hï Ibiza works for a trip">
              <p>
                Tuesday is one of the smartest nights to build an Ibiza trip around. It gives you a clean structure: arrive Monday, settle into the island, use Tuesday for Eastenderz, then choose whether the rest of the trip becomes a short midweek escape or a longer run through the island's club calendar.
              </p>
              <p>
                Hï Ibiza is in Playa d'en Bossa, which means the easiest hotel areas are Playa d'en Bossa, Ibiza Town and Talamanca. Playa d'en Bossa keeps you closest to Hï, Ushuaïa, beach clubs and late-night taxis. Ibiza Town gives you restaurants, marina energy and pre-drinks. Talamanca works if you want something a little more polished while still staying close to the action.
              </p>
              <p>
                For groups, this is where HLT can help. Some people only want a ticket link. Others want the night built properly: hotel, transfers, tables, beach clubs, villa options, after-party planning and a proper route through the week.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="View Eastenderz tickets" />
                <ConciergeCta children="Ask HLT to plan the night" />
              </div>
            </ArticleSection>

            <ArticleSection title="Event details at a glance">
              <ul className="grid gap-2 text-slate-300">
                <li><strong className="text-white">Event:</strong> East End Dubs presents Eastenderz</li>
                <li><strong className="text-white">Venue:</strong> Hï Ibiza, Playa d'en Bossa</li>
                <li><strong className="text-white">Date:</strong> Tuesday 16 June 2026</li>
                <li><strong className="text-white">Time:</strong> 23:30</li>
                <li><strong className="text-white">Theatre:</strong> East End Dubs B2B Sosa, Marsolo, DXNBY</li>
                <li><strong className="text-white">Club Room / NOW HERE:</strong> Paco Osuna, DJ Oliver, Manu Gonzalez</li>
                <li><strong className="text-white">Music:</strong> Deep tech, minimal house, tech house and groove-led underground house</li>
                <li><strong className="text-white">Tickets:</strong> Available through HLT's ticket direction link</li>
              </ul>
            </ArticleSection>

            <ArticleSection title="Final word">
              <p>
                Eastenderz at Hï Ibiza on 16 June is the type of date that rewards people who plan their Ibiza trip around music first. The lineup is specific, the sound is clear, and the venue gives it the scale to feel like a proper Tuesday-night centrepiece.
              </p>
              <p>
                If you are on the island tonight, this is one of the strongest underground house options to look at. If you are planning later in the season, use this as the model: pick the right event first, then build the hotel, transfers and full trip around the night.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="View Eastenderz Hï Ibiza tickets" />
                <Link to={EVENTS_URL} className="btn-secondary">Browse Ibiza events -&gt;</Link>
              </div>
            </ArticleSection>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-500/20 bg-[#111111] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">HLT trip builder</p>
              <h3 className="mt-3 font-display text-3xl leading-none text-white">Build the night properly.</h3>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Need a hotel, villa, table, transfers or group itinerary around Eastenderz? Send the request and HLT can shape the trip around the event.
              </p>
              <div className="mt-4 grid gap-3">
                <TicketCta children="Ticket link" />
                <ConciergeCta children="Concierge request" />
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Focus keywords</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {KEYWORDS.slice(0, 10).map((keyword) => (
                  <span key={keyword} className="bg-white/5 px-2 py-1 text-[0.68rem] uppercase tracking-[0.12em] text-slate-300">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="space-y-4 border-t border-white/10 pt-8">
          <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
          <div className="grid gap-3">
            {FAQS.map((item) => (
              <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                <p className="mt-3 text-sm leading-6 text-slate-300">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </article>
    </>
  );
}
